import { getNavIcon } from "../config/navIconRegistry";
import { hasLayoutPermission } from "../config/vehiclesModuleCatalog";
import { isRouteEyeAdmin } from "../utils/isRouteEyeAdmin";

const ROUTE_EYE_SUPERADMIN_NAV_ITEMS = [
  {
    id: "organization",
    path: "/org",
    label: "Organization",
    iconKey: "fleet.nav.organization",
  },
  {
    id: "features",
    path: "/feature",
    label: "Features",
    iconKey: "fleet.nav.feature",
  },
];

const ORGANIZATION_PATHS = new Set([
  "/organization/add",
  "/AddOrganization",
  "/Organization",
]);

const ORGANIZATION_NAV_PATHS = new Set(["/dashboard", "/user", "/alert"]);

const INACTIVE_ORG_HIDDEN_NAV_PATHS = new Set([
  "/alert",
  "/reports",
  "/school-alerts",
  "/school-reports",
]);

const INACTIVE_ORG_HIDDEN_SUBNAV_PATHS = new Set([
  "/vehicle/add",
  "/vehicle/TrackAllVehicle",
  "/route/add",
  "/route/allroutes",
  "/stop/add",
  "/stop/all/map",
  "/device/add",
  "/device/monitor",
  "/schedule/add",
  "/driver/add",
  "/user/AddUser",
  "/user/role",
  "/site/add",
]);

export function isInactiveOrgHiddenNavPath(path = "") {
  const normalized = String(path || "").split("?")[0];
  if (INACTIVE_ORG_HIDDEN_NAV_PATHS.has(normalized)) {
    return true;
  }

  return (
    normalized.startsWith("/alert") ||
    normalized.startsWith("/reports") ||
    normalized.startsWith("/school-alerts") ||
    normalized.startsWith("/school-reports")
  );
}

function isInactiveOrgHiddenNavItem(item = {}) {
  const path = String(item.path || "").split("?")[0];
  if (isInactiveOrgHiddenNavPath(path) || INACTIVE_ORG_HIDDEN_SUBNAV_PATHS.has(path)) {
    return true;
  }

  const label = String(item.label || item.labelKey || "").toLowerCase();
  const id = String(item.id || item.actionKey || "").toLowerCase();
  const haystack = `${label} ${id}`;

  return (
    haystack.includes("monitor") ||
    haystack.includes("live device") ||
    haystack.includes("create ") ||
    haystack.startsWith("create")
  );
}

export function filterNavForInactiveOrganization(navItems = [], organizationInactive) {
  if (!organizationInactive) {
    return navItems;
  }

  return navItems
    .filter((item) => !isInactiveOrgHiddenNavItem(item))
    .map((item) => {
      const nestedKeys = ["subItems", "children"].filter((key) =>
        Array.isArray(item[key]),
      );
      if (!nestedKeys.length) {
        return item;
      }

      const nextItem = { ...item };
      nestedKeys.forEach((key) => {
        const filtered = filterNavForInactiveOrganization(item[key], true);
        nextItem[key] = filtered.length ? filtered : undefined;
      });
      return nextItem;
    });
}

function getNavBarSection(layout) {
  if (!layout?.sections) return null;
  return layout.sections.find((section) => section.type === "navBar") || null;
}


function isNavItemVisibleForRole(widget, roleKey) {
  if (!Array.isArray(widget?.visibleFor) || widget.visibleFor.length === 0) {
    return true;
  }

  return widget.visibleFor.includes(roleKey);
}

function buildNavItem(widget, { hasPermission, resolveAction, roleKey }) {
  if (!isNavItemVisibleForRole(widget, roleKey)) {
    return null;
  }

  if (widget.permission && !hasLayoutPermission(widget.permission, hasPermission)) {
    return null;
  }

  const action = widget.actionKey ? resolveAction(widget.actionKey) : null;
  const path = widget.path || action?.path || "#";

  const children = Array.isArray(widget.children)
    ? widget.children
        .map((child) => buildNavItem(child, { hasPermission, resolveAction, roleKey }))
        .filter(Boolean)
    : [];

  return {
    id: widget.id,
    path,
    label: widget.labelKey,
    icon: getNavIcon(widget.iconKey, widget.labelKey),
    showBadge: Boolean(widget.showBadge),
    onClick: action?.onClick || null,
    subItems: children.length > 0 ? children : undefined,
  };
}

export function ensureFleetAttendantNavItem(layout) {
  if (!layout?.sections) return layout;

  const sections = layout.sections.map((section) => {
    if (section?.type !== "navBar" || !Array.isArray(section.widgets)) {
      return section;
    }

    const alreadyPresent = section.widgets.some(
      (widget) => widget?.id === "attendants" || widget?.path === "/attendants",
    );
    if (alreadyPresent) return section;

    const driversIndex = section.widgets.findIndex(
      (widget) => widget?.id === "drivers" || widget?.path === "/driver",
    );
    const sitesIndex = section.widgets.findIndex(
      (widget) => widget?.id === "sites" || widget?.path === "/site",
    );
    const insertAt =
      driversIndex >= 0
        ? driversIndex + 1
        : sitesIndex >= 0
          ? sitesIndex
          : section.widgets.length;
    const driverOrder = Number(section.widgets[driversIndex]?.order);
    const siteOrder = Number(section.widgets[sitesIndex]?.order);
    const order =
      Number.isFinite(driverOrder) && Number.isFinite(siteOrder)
        ? (driverOrder + siteOrder) / 2
        : Number.isFinite(driverOrder)
          ? driverOrder + 0.5
          : 6.5;

    const widgets = [...section.widgets];
    widgets.splice(insertAt, 0, {
      id: "attendants",
      type: "navItem",
      labelKey: "Attendants",
      path: "/attendants",
      iconKey: "fleet.nav.attendant",
      order,
      permission: {
        apiPath: "/api/employee",
        action: "GET",
      },
    });

    return { ...section, widgets };
  });

  return { ...layout, sections };
}

export function buildFleetNavFromLayout(layout, { hasPermission, resolveAction, roleKey = null }) {
  const navSection = getNavBarSection(layout);
  if (!navSection?.widgets) return [];

  return navSection.widgets
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((widget) => buildNavItem(widget, { hasPermission, resolveAction, roleKey }))
    .filter(Boolean);
}

function buildRouteEyeSuperAdminNavItems() {
  return ROUTE_EYE_SUPERADMIN_NAV_ITEMS.map((item) => ({
    id: item.id,
    path: item.path,
    label: item.label,
    icon: getNavIcon(item.iconKey, item.label),
    showBadge: false,
    onClick: null,
  }));
}

export function injectRouteEyeSuperAdminNavItems(navItems, pathname) {
  if (!isRouteEyeAdmin() || ORGANIZATION_PATHS.has(pathname)) {
    return navItems;
  }

  const superAdminItems = buildRouteEyeSuperAdminNavItems();
  const reportsIndex = navItems.findIndex((item) => item.path === "/reports");

  if (reportsIndex === -1) {
    return [...navItems, ...superAdminItems];
  }

  return [
    ...navItems.slice(0, reportsIndex),
    ...superAdminItems,
    ...navItems.slice(reportsIndex),
  ];
}

export function filterNavForOrganizationPath(navItems, pathname, hasPermission) {
  if (!ORGANIZATION_PATHS.has(pathname)) {
    return navItems;
  }

  return navItems.filter((item) => {
    if (!ORGANIZATION_NAV_PATHS.has(item.path)) {
      return false;
    }

    if (item.path === "/alert") {
      return hasPermission("/api/alert");
    }

    if (item.path === "/user") {
      return hasPermission("/api/user");
    }

    return true;
  });
}

export const isWriteAction = (action) => {
  const normalized = String(action || "").toUpperCase();
  return (
    normalized === "POST" ||
    normalized === "PUT" ||
    normalized === "PATCH" ||
    normalized === "DELETE"
  );
};

export function createHasPermission(features, { writesDisabled = false } = {}) {
  return (requiredPath, requiredAction = null) => {
    if (writesDisabled && isWriteAction(requiredAction)) {
      return false;
    }

    if (!features || !requiredPath) {
      return true;
    }

    return features.some((feature) => {
      const pathMatches = String(feature?.api_path || "").includes(requiredPath);
      return requiredAction
        ? pathMatches && feature.action === requiredAction
        : pathMatches;
    });
  };
}
